import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingDown } from "lucide-react";
import api from "../lib/api";
import { fmtEUR, fmtInt, fmtPct, fmtRoas, fmtDataIT } from "../lib/format";
import { InfoTip } from "../components/InfoTip";

// --- Stile dashboard (19/09/2026): canvas grigio chiaro, riquadri bianchi,
// niente sotto i 15px, numeri principali 36-40px, verde solo su denaro entrato,
// ambra su ciò che è in attesa, rosso solo per problemi veri.
const FONT = { fontFamily: "'Instrument Sans', sans-serif" };
const LBL = "text-[15px] font-semibold uppercase tracking-wider text-[#5A6473]";
const VAL = "text-4xl font-extrabold font-mono tracking-tight tnum";
const TONE_TXT = { dark: "text-[#161A20]", green: "text-[#0F766E]", amber: "text-[#A96A12]", red: "text-red-700" };
const boxCls = (tone) =>
  tone === "amber" ? "bg-[#FDF9F0] border-[#EBD6B2]"
    : tone === "red" ? "bg-red-50/60 border-red-200"
      : "bg-white border-[#DDE1E7]";

const Blocco = ({ n, titolo, children }) => (
  <section>
    <h2 className="text-[15px] font-bold uppercase tracking-widest text-[#3A424E] mb-2.5">{n} · {titolo}</h2>
    {children}
  </section>
);

const Delta = ({ testid, delta }) => (
  delta != null && isFinite(delta) ? (
    <div className="mt-2">
      <span data-testid={`${testid}-delta`} className={`text-[15px] font-mono rounded-full px-2.5 py-1 ${delta >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
        {delta >= 0 ? "+" : ""}{(delta * 100).toLocaleString("it-IT", { maximumFractionDigits: 1 })}% vs periodo precedente
      </span>
    </div>
  ) : null
);

const BigKpi = ({ label, value, testid, tip, sub, tone = "dark", delta }) => (
  <div data-testid={testid} className={`rounded-[14px] border p-5 fade-in ${boxCls(tone)}`}>
    <div className={LBL}>{label}<InfoTip text={tip} testid={`${testid}-tip`} /></div>
    <div className={`${VAL} mt-2 ${TONE_TXT[tone]}`}>{value}</div>
    {sub && <div className="text-[15px] text-[#5A6473] mt-1.5">{sub}</div>}
    <Delta testid={testid} delta={delta} />
  </div>
);

const MiniKpi = ({ label, value, testid, tip, tone = "dark", delta }) => (
  <div data-testid={testid} className={`rounded-[14px] border p-4 fade-in ${boxCls(tone)}`}>
    <div className={LBL}>{label}<InfoTip text={tip} testid={`${testid}-tip`} /></div>
    <div className={`text-2xl font-extrabold font-mono tracking-tight mt-1.5 tnum ${TONE_TXT[tone]}`}>{value}</div>
    <Delta testid={testid} delta={delta} />
  </div>
);

// Fascia sempre visibile, FUORI dal filtro di periodo.
const FasciaDaIncassare = ({ d }) => (
  <div data-testid="kpi-card-da-incassare-sempre" className="rounded-[14px] border border-[#EBD6B2] bg-[#FDF9F0] p-5 fade-in">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[15px] font-bold uppercase tracking-widest text-[#A96A12]">Da incassare · tutti i periodi</span>
      <InfoTip text="Questo riquadro NON segue il filtro di periodo: somma tutti i contrassegni ancora aperti di qualunque mese, al netto dei resi. I blocchi sotto invece coprono solo il periodo selezionato" testid="da-incassare-sempre-tip" />
      <Link to="/cassa" data-testid="da-incassare-sempre-link"
        className="ml-auto text-[15px] font-semibold text-[#A96A12] hover:text-amber-900 underline decoration-amber-300 transition-colors">Dettaglio in Cassa COD →</Link>
    </div>
    <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mt-2">
      <div data-testid="da-incassare-sempre-totale" className={`${VAL} text-[#A96A12]`}>{fmtEUR(d.totale)}</div>
      <div className="text-[15px] text-[#5A6473] pb-1" data-testid="da-incassare-sempre-stati">
        consegnati in attesa di versamento <span className="font-mono font-semibold tnum">{fmtEUR(d.per_stato.consegnato_non_incassato.importo)}</span>
        {" "}· in viaggio <span className="font-mono font-semibold tnum">{fmtEUR(d.per_stato.in_transito.importo)}</span>
        {" "}· {fmtInt(d.ordini)} ordini aperti
      </div>
    </div>
    {d.oltre_10gg.length > 0 && (
      <div data-testid="da-incassare-sempre-oltre10" className="mt-3 text-[15px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
        ⚠ {fmtEUR(d.oltre_10gg.reduce((a, x) => a + x.importo, 0))} consegnati da oltre 10 giorni senza accredito — da sollecitare al corriere
      </div>
    )}
  </div>
);

const RoasCard = ({ t }) => (
  <div data-testid="kpi-card-roas" className={`rounded-[14px] border p-5 fade-in ${boxCls("dark")}`}>
    <div className={LBL}>
      ROAS — ritorno sulla spesa pubblicitaria
      <InfoTip text="Sul venduto (predittivo): fatturato o suo imponibile ÷ spesa ADV — dice come sta andando la pubblicità ADESSO. Sull'incassato (consuntivo): cosa è già rientrato in cassa. Vanno letti insieme" testid="kpi-card-roas-tip" />
    </div>
    {t.adv > 0 ? (
      <div className="mt-2 space-y-3">
        <div className="grid grid-cols-2 divide-x divide-slate-100">
          <div data-testid="kpi-card-roas-venduto-lordo" className="pr-3 text-right">
            <div className="text-[15px] text-[#5A6473] uppercase tracking-wide font-semibold">ROAS lordo sul venduto</div>
            <div className="text-2xl font-extrabold font-mono tnum text-[#161A20]">{fmtRoas(t.roas_venduto_lordo)}</div>
          </div>
          <div data-testid="kpi-card-roas-venduto-netto" className="pl-3 text-right">
            <div className="text-[15px] text-[#5A6473] uppercase tracking-wide font-semibold">ROAS netto IVA sul venduto</div>
            <div className="text-2xl font-extrabold font-mono tnum text-[#161A20]">{fmtRoas(t.roas_venduto_netto)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-slate-100 border-t border-slate-100 pt-3">
          <div data-testid="kpi-card-roas-lordo" className="pr-3 text-right">
            <div className="text-[15px] text-[#5A6473] uppercase tracking-wide">ROAS lordo sull'incassato</div>
            <div className="text-xl font-bold font-mono tnum text-slate-600">{fmtRoas(t.roas_lordo)}</div>
          </div>
          <div data-testid="kpi-card-roas-netto-iva" className="pl-3 text-right">
            <div className="text-[15px] text-[#5A6473] uppercase tracking-wide">ROAS netto IVA sull'incassato</div>
            <div className="text-xl font-bold font-mono tnum text-slate-600">{fmtRoas(t.roas_netto_iva)}</div>
          </div>
        </div>
      </div>
    ) : (
      <p data-testid="roas-non-calcolabile" className="text-[15px] text-[#5A6473] mt-2">
        Non calcolabile: spesa pubblicitaria assente nel periodo. Inseriscila dalla pagina Spesa ADV per vedere il ROAS sul venduto e sull'incassato.
      </p>
    )}
  </div>
);

// Blocco 2 — una sola tabella con tutte le uscite e il totale in fondo.
const UsciteTable = ({ t, isMaster, consolidato, storeProprio, giorni, onMerceDettaglio }) => {
  const figli = t.merce_figli;
  const merceMain = consolidato ? (figli?.cession ?? t.atteso_merce) : t.atteso_merce;
  const resi = t.resi_periodo || { n: 0, importo: 0, soldi_restituiti: 0 };
  const merceDet = isMaster
    ? (storeProprio
      ? null
      : consolidato && figli
        ? <>costo reale <span data-testid="merce-reale" className="font-semibold">{fmtEUR(t.merce_reale)}</span> · ricarico <span data-testid="merce-ricarico" className="font-semibold">{fmtEUR(figli.ricarico)}</span></>
        : <>costo reale <span data-testid="merce-reale" className="font-semibold">{fmtEUR(t.merce_reale)}</span> · ricarico <span data-testid="merce-ricarico" className="font-semibold">{fmtEUR(t.ricarico_merce)}</span></>)
    : null;
  const spedDet = isMaster && t.atteso_margine_spedizioni != null
    ? <>costo reale {t.affidabilita_spedizioni ? <span data-testid="kpi-card-affidabilita" className="font-semibold">{fmtEUR(t.affidabilita_spedizioni.reale_val)}</span> : "—"}
      {" · "}margine <span data-testid="kpi-card-margine-spedizioni" className={`font-semibold ${t.atteso_margine_spedizioni < 0 ? "text-red-700" : "text-[#0F766E]"}`}>{fmtEUR(t.atteso_margine_spedizioni)}</span></>
    : null;
  const rows = [
    { key: "merce", label: "Merce", value: merceMain, testid: "merce-cessione", det: merceDet },
    { key: "spedizioni", label: "Spedizioni", value: t.atteso_spedizioni_addebitate, testid: "kpi-card-spedizioni", det: spedDet },
    { key: "adv", label: "Pubblicità", value: t.adv, testid: "kpi-card-adv", det: giorni > 0 ? `media ${fmtEUR(t.adv / giorni)} al giorno` : null },
    { key: "gateway", label: "Commissioni gateway", value: t.commissioni_gateway, testid: "kpi-card-commissioni", det: null },
    { key: "resi", label: "Resi e rimborsi", value: resi.importo, testid: "kpi-card-resi", det: resi.n > 0 ? `${resi.n} resi nel periodo · denaro restituito ${fmtEUR(resi.soldi_restituiti)}` : null },
  ];
  const totale = rows.reduce((s, r) => s + (r.value || 0), 0);
  return (
    <div className={`rounded-[14px] border overflow-hidden fade-in ${boxCls("dark")}`}>
      <table className="w-full" data-testid="uscite-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-testid={`uscite-row-${r.key}`} className="border-b border-[#EEF0F4] last:border-0">
              <td className="px-5 py-3.5">
                <div className="text-[15px] font-semibold uppercase tracking-wider text-[#5A6473]">{r.label}</div>
                {r.det && <div className="text-[15px] text-[#5A6473] mt-0.5 font-mono tnum">{r.det}</div>}
                {r.key === "merce" && isMaster && !storeProprio && (
                  <button data-testid="merce-dettaglio-btn" onClick={onMerceDettaglio}
                    className="text-[15px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-150 mt-0.5">
                    Dettaglio per prodotto →
                  </button>
                )}
              </td>
              <td className="px-5 py-3.5 text-right">
                <span data-testid={r.testid} className="text-2xl font-extrabold font-mono tracking-tight tnum text-[#161A20]">{fmtEUR(r.value)}</span>
              </td>
            </tr>
          ))}
          <tr data-testid="uscite-row-totale" className="bg-slate-50/70 border-t-2 border-[#DDE1E7]">
            <td className="px-5 py-4 text-[15px] font-bold uppercase tracking-wider text-[#161A20]">Totale uscite</td>
            <td className="px-5 py-4 text-right">
              <span data-testid="uscite-totale" className="text-3xl font-extrabold font-mono tracking-tight tnum text-[#161A20]">{fmtEUR(totale)}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const VendutoPersoCard = ({ t, isMaster, onDettaglio }) => {
  const vp = t.venduto_perso;
  if (!vp || !vp.n) return null;
  return (
    <div data-testid="kpi-card-venduto-perso" className="rounded-[14px] border border-red-200 bg-red-50/60 p-5 fade-in min-w-0 overflow-hidden">
      <div className="flex items-center gap-1.5 text-[15px] font-semibold uppercase tracking-wider text-red-700">
        <TrendingDown className="w-4 h-4 shrink-0" />
        Venduto perso
        <InfoTip text="Ordini che non incasseranno mai: annullati dopo la spedizione, rifiutati o rientrati a magazzino, resi totali, rimborsi integrali. I costi sostenuti a vuoto (merce + spedizioni reali) sono visibili solo al proprietario" testid="kpi-card-venduto-perso-tip" />
      </div>
      <div data-testid="venduto-perso-lordo" className={`${VAL} mt-2 text-red-700`}>{fmtEUR(vp.lordo)}</div>
      <div data-testid="venduto-perso-n" className="text-[15px] text-red-900/60 mt-1">
        {fmtInt(vp.n)} ordini · {fmtPct(vp.pct_fatturato)} del fatturato
      </div>
      {isMaster && vp.costi_sostenuti != null && (
        <div className="text-[15px] text-red-900/70 mt-1">
          Costi sostenuti a vuoto: <span data-testid="venduto-perso-costi" className="font-mono tnum font-semibold text-red-700">{fmtEUR(vp.costi_sostenuti)}</span>
        </div>
      )}
      {t.tasso_rifiuto_storico != null && (
        <div data-testid="venduto-perso-tasso" className="text-[15px] text-red-900/60 mt-1">
          Tasso di rifiuto storico: <span className="font-mono font-semibold">{fmtPct(t.tasso_rifiuto_storico)}</span>
          {t.tasso_rifiuto_da_storico ? " (reale, su ordini chiusi)" : " (stima configurata — campione insufficiente)"}
        </div>
      )}
      <button data-testid="venduto-perso-dettaglio-btn" onClick={onDettaglio}
        className="text-[15px] font-semibold text-red-700 hover:text-red-900 transition-colors duration-150 mt-1.5">
        Dettaglio ordini persi →
      </button>
    </div>
  );
};

const CAUSE_PERSO = {
  annullato_dopo_spedizione: "Annullato dopo la spedizione",
  rientro_magazzino: "Rientro a magazzino (rifiutato)",
  reso_totale: "Reso totale",
  rimborso_integrale: "Rimborso integrale",
  contrassegno_annullato: "Contrassegno annullato dal corriere",
};

const SocioVersatoCard = () => {
  const [tot, setTot] = useState(null);
  useEffect(() => {
    api.get("/quote").then((r) => {
      const p = (r.data.partecipanti || [])[0];
      setTot(p ? p.totale_versato : 0);
    }).catch(() => {});
  }, []);
  if (tot == null) return null;
  return (
    <BigKpi label="Totale versato" value={fmtEUR(tot)} testid="kpi-card-totale-versato" tone="green"
      sub="dal luglio 2024 a oggi"
      tip="Somma di tutti i versamenti che hai ricevuto dal luglio 2024 a oggi, storico incluso. Il dettaglio mese per mese è in Quote soci" />
  );
};

const VendutoPersoDialog = ({ storeId, from, to, isMaster, consolidato, onClose }) => {
  const [ordini, setOrdini] = useState(null);
  useEffect(() => {
    api.get(`/venduto-perso?store_id=${storeId}&from=${from}&to=${to}`)
      .then((r) => setOrdini(r.data.ordini)).catch(() => setOrdini([]));
  }, [storeId, from, to]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="venduto-perso-dialog">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-slate-200 w-full max-w-4xl max-h-[85vh] flex flex-col fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900">Venduto perso — dettaglio ordini</h3>
          <button onClick={onClose} data-testid="venduto-perso-chiudi" className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <p className="px-5 pt-3 text-xs text-slate-400">
          Ordini che non incasseranno mai{isMaster ? ", con i costi reali sostenuti a vuoto (merce fornitore + spedizioni corriere)" : ""}. Utile per individuare clienti o cause ricorrenti.
        </p>
        <div className="overflow-y-auto px-5 py-3">
          {!ordini ? <p className="text-sm text-slate-400 py-8 text-center">Caricamento…</p> : ordini.length === 0 ? (
            <p data-testid="venduto-perso-vuoto" className="text-sm text-slate-400 py-8 text-center">Nessun ordine perso nel periodo.</p>
          ) : (
            <table className="w-full text-sm" data-testid="venduto-perso-table">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left border-b border-slate-200">
                  <th className="py-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">Ordine</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Data</th>
                  {consolidato && <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Store</th>}
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Cliente</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Causa</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Importo perso</th>
                  {isMaster && <th className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Costi a vuoto</th>}
                </tr>
              </thead>
              <tbody>
                {ordini.map((o) => (
                  <tr key={o.id} data-testid={`venduto-perso-row-${o.id_ordine_esterno || o.id}`} className="border-b border-slate-50">
                    <td className="py-1.5 font-mono text-xs">{o.id_ordine_esterno || o.id.slice(0, 8)}</td>
                    <td className="font-mono text-xs">{o.data}</td>
                    {consolidato && <td className="text-xs text-slate-600">{o.store_nome}</td>}
                    <td className="text-xs text-slate-600 pr-2">{o.nome_cliente || "—"}</td>
                    <td className="text-xs"><span className="bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap">{CAUSE_PERSO[o.causa] || o.causa}</span></td>
                    <td className="font-mono text-xs text-right tnum">{fmtEUR(o.lordo)}</td>
                    {isMaster && <td className="font-mono text-xs text-right tnum text-red-700">{fmtEUR((o.merce || 0) + (o.spedizione || 0))}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const MerceDettaglioDialog = ({ storeId, from, to, onClose }) => {
  const [prodotti, setProdotti] = useState(null);
  useEffect(() => {
    api.get(`/dashboard/ricarico-prodotti?store_id=${storeId}&from=${from}&to=${to}`)
      .then((r) => setProdotti(r.data.prodotti)).catch(() => setProdotti([]));
  }, [storeId, from, to]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="merce-dettaglio-dialog">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-slate-200 w-full max-w-4xl max-h-[85vh] flex flex-col fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900">Ricarico merce per prodotto</h3>
          <button onClick={onClose} data-testid="merce-dettaglio-chiudi" className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <p className="px-5 pt-3 text-xs text-slate-400">
          Tutti gli ordini confermati del periodo, ordinato per ricarico crescente: in cima i prodotti che erodono il margine.
          Il segno ≈ sul costo reale indica valori ricostruiti con la regola 4/5 € (stimati). "Dati parziali" = righe senza cessione o senza costo reale nel periodo.
        </p>
        <div className="overflow-y-auto px-5 py-3">
          {!prodotti ? <p className="text-sm text-slate-400 py-8 text-center">Caricamento…</p> : prodotti.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Nessun prodotto ceduto nel periodo.</p>
          ) : (
            <table className="w-full text-sm" data-testid="merce-dettaglio-table">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left border-b border-slate-200">
                  <th className="py-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">Prodotto</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Varianti</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Pezzi</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Cessione</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Costo reale</th>
                  <th className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Ricarico</th>
                </tr>
              </thead>
              <tbody>
                {prodotti.map((g) => (
                  <tr key={g.prodotto} data-testid={`merce-dettaglio-row-${g.prodotto.slice(0, 24)}`} className="border-b border-slate-50">
                    <td className="py-1.5 text-xs pr-2">
                      {g.prodotto}
                      {(g.senza_cessione > 0 || g.senza_costo_reale > 0) && (
                        <span className="ml-1.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">dati parziali</span>
                      )}
                    </td>
                    <td className="font-mono text-xs text-right">{g.varianti}</td>
                    <td className="font-mono text-xs text-right">{g.qta}</td>
                    <td className="font-mono text-xs text-right tnum">{fmtEUR(g.cessione)}</td>
                    <td className="font-mono text-xs text-right tnum">
                      {(g.origine_costo === "ricostruito" || g.origine_costo === "mista") ? "≈ " : ""}{fmtEUR(g.costo_reale)}
                    </td>
                    <td className={`font-mono text-xs text-right tnum font-semibold ${g.ricarico < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtEUR(g.ricarico)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const Skeleton = () => (
  <div data-testid="dashboard-skeleton" className="space-y-4">
    <div className="skeleton h-8 w-64" />
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-32" />)}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-20" />)}
    </div>
    <div className="skeleton h-64" />
  </div>
);

export default function DashboardPage() {
  const { storeId, from, to, isMaster } = useOutletContext();
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [ordini, setOrdini] = useState([]);
  const [mode, setMode] = useState("famiglia");
  const [showMerceDettaglio, setShowMerceDettaglio] = useState(false);
  const [showVendutoPerso, setShowVendutoPerso] = useState(false);

  const prevRange = useMemo(() => {
    const d0 = new Date(from + "T00:00:00");
    const d1 = new Date(to + "T00:00:00");
    const days = Math.round((d1 - d0) / 86400000) + 1;
    const p1 = new Date(d0);
    p1.setDate(p1.getDate() - 1);
    const p0 = new Date(p1);
    p0.setDate(p0.getDate() - days + 1);
    const f = (d) => d.toISOString().slice(0, 10);
    return { from: f(p0), to: f(p1) };
  }, [from, to]);

  useEffect(() => {
    setData(null);
    setPrevData(null);
    api.get(`/dashboard?store_id=${storeId}&from=${from}&to=${to}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/dashboard?store_id=${storeId}&from=${prevRange.from}&to=${prevRange.to}`)
      .then((r) => setPrevData(r.data)).catch(() => {});
    api.get(`/ordini?store_id=${storeId}&from=${from}&to=${to}&per_page=200`)
      .then((r) => setOrdini(r.data.risultati)).catch(() => setOrdini([]));
  }, [storeId, from, to, prevRange]);

  const serie = useMemo(() => {
    const perGiorno = {};
    ordini.forEach((o) => {
      perGiorno[o.data] = (perGiorno[o.data] || 0) + (o.calc?.totale_incassato || 0);
    });
    return Object.entries(perGiorno).sort(([a], [b]) => a.localeCompare(b))
      .map(([g, v]) => ({ g: g.slice(8) + "/" + g.slice(5, 7), incasso: Math.round(v * 100) / 100 }));
  }, [ordini]);

  if (!data) return <Skeleton />;
  const t = data.totali;
  const pt = prevData?.totali;
  const delta = (cur, prev) => (prev && prev !== 0 ? (cur - prev) / Math.abs(prev) : null);
  const pagamenti = data.pagamenti[mode];
  const giorni = Math.max(1, Math.round((new Date(to + "T00:00:00") - new Date(from + "T00:00:00")) / 86400000) + 1);
  const quotaCOD = (data.pagamenti?.famiglia || []).find((p) => p.chiave === "Contrassegno")?.quota;
  const storeProprio = !data.consolidato && ["master", "proprio"].includes(data.store?.tipo);

  return (
    <div data-testid="dashboard-page" className="space-y-6 rounded-2xl bg-[#F2F3F5] -mx-2 sm:-mx-3 p-4 sm:p-6" style={FONT}>
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#161A20]">
          {data.consolidato ? "Consolidato — tutti gli store" : data.store?.nome}
        </h1>
        <span className="text-[15px] text-[#5A6473] font-mono">{fmtDataIT(data.periodo.from)} → {fmtDataIT(data.periodo.to)}</span>
      </div>

      {data.da_incassare_sempre && <FasciaDaIncassare d={data.da_incassare_sempre} />}

      <Blocco n={1} titolo="Cosa è entrato">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <BigKpi label="Fatturato" value={fmtEUR(t.fatturato)} testid="kpi-card-fatturato"
            delta={delta(t.fatturato, pt?.fatturato)}
            tip="Quanto è stato venduto nel periodo: somma dei totali lordi degli ordini, al netto degli annullati. Se un ordine viene annullato, il fatturato scende" />
          <BigKpi label="Incassato" value={fmtEUR(t.incasso)} testid="kpi-card-incasso" tone="green"
            delta={delta(t.incasso, pt?.incasso)}
            sub={<>Tasso di incasso <span data-testid="kpi-card-tasso-incasso" className="font-mono font-semibold">{fmtPct(t.tasso_incasso)}</span></>}
            tip="Soldi realmente entrati sul conto nel periodo. Il tasso di incasso è la quota del fatturato già entrata: nei periodi recenti è fisiologicamente bassa perché i contrassegni entrano quando il corriere versa (5–7 giorni dalla consegna)" />
          <BigKpi label="Da incassare (periodo)" value={fmtEUR(t.da_incassare)} testid="kpi-card-da-incassare" tone="amber"
            tip="Fatturato meno incassato, solo per il periodo selezionato: venduto non ancora entrato (tipicamente contrassegni in viaggio). Il totale di TUTTI i mesi è nella fascia ambra qui sopra" />
          <VendutoPersoCard t={t} isMaster={isMaster} onDettaglio={() => setShowVendutoPerso(true)} />
          {!isMaster && <SocioVersatoCard />}
        </div>
      </Blocco>

      <Blocco n={2} titolo="Cosa è uscito">
        <UsciteTable t={t} isMaster={isMaster} consolidato={data.consolidato} storeProprio={storeProprio}
          giorni={giorni} onMerceDettaglio={() => setShowMerceDettaglio(true)} />
      </Blocco>

      <Blocco n={3} titolo="Cosa resta">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <BigKpi label="Utile realizzato (cassa)" value={fmtEUR(t.guadagno_netto)} testid="kpi-card-guadagno-netto"
            tone={t.guadagno_netto < 0 ? "red" : "green"} delta={delta(t.guadagno_netto, pt?.guadagno_netto)}
            tip="Solo ordini effettivamente incassati, con i loro costi: imponibile meno merce, spedizioni addebitate e spesa ADV. È la verità di cassa e la base delle quote socio — nessun socio matura su denaro non entrato" />
          {isMaster && t.atteso_netto != null && (
            <BigKpi label="Utile atteso (competenza)" value={fmtEUR(t.atteso_netto)} testid="kpi-card-utile-atteso" tone="amber"
              tip="Tutti gli ordini confermati del periodo, incassati o no: ricavo pieno, merce sempre (già pagata al fornitore), spedizione al costo reale o stimato dai parametri dello store. Risponde a: se tutto arriva a destinazione, quanto ho guadagnato" />
          )}
          {isMaster && t.atteso_ponderato != null && (
            <BigKpi label={`Utile atteso ponderato${t.tasso_atteso_da_storico === false ? " (tasso stimato)" : ""}`}
              value={fmtEUR(t.atteso_ponderato)} testid="kpi-card-utile-ponderato" tone="amber"
              sub={<>Margine per pezzo: <span data-testid="kpi-card-margine-pezzo" className="font-mono font-semibold">{fmtEUR(t.atteso_margine_per_pezzo)}</span></>}
              tip="Utile atteso corretto col tasso di incasso dello store: i contrassegni rifiutati alla consegna non incassano mai, ma merce e spedizione sono già pagati. Il margine per pezzo è l'utile atteso diviso i pezzi venduti (le righe non-merce non contano come pezzi)" />
          )}
          {!isMaster && (
            <BigKpi label="Margine per pezzo" value={fmtEUR(t.atteso_margine_per_pezzo)} testid="kpi-card-margine-pezzo"
              tone={t.atteso_margine_per_pezzo < 0 ? "red" : "dark"}
              tip="Utile atteso diviso i pezzi di tutti gli ordini confermati (le righe non-merce non contano come pezzi). Base predittiva: si affina da sola man mano che le consegne si chiudono" />
          )}
        </div>
      </Blocco>

      <Blocco n={4} titolo="Pubblicità">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <BigKpi label="Spesa pubblicitaria" value={fmtEUR(t.adv)} testid="kpi-card-adv-hero"
            sub={`Media ${fmtEUR(t.adv / giorni)} al giorno nel periodo`}
            tip="Spesa pubblicitaria del periodo (inserimenti manuali o import CSV dalla pagina Spesa ADV)" />
          <RoasCard t={t} />
        </div>
      </Blocco>

      <Blocco n={5} titolo="Numeri del negozio">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <MiniKpi label="Ordini" value={fmtInt(t.num_ordini)} testid="kpi-card-ordini" />
          <MiniKpi label="Pezzi venduti" value={fmtInt(t.pezzi_venduti)} testid="kpi-card-pezzi"
            tip="Solo merce: le righe non-merce (es. commissione contrassegno) non contano come pezzi" />
          <MiniKpi label="Scontrino medio" value={fmtEUR(t.scontrino_medio)} testid="kpi-card-scontrino"
            tip="Incassato medio per ordine incassato nel periodo" />
          <MiniKpi label="Pezzi per ordine" value={t.pezzi_per_ordine} testid="kpi-card-pezzi-ordine" />
          {quotaCOD != null && (
            <MiniKpi label="Quota contrassegno" value={fmtPct(quotaCOD)} testid="kpi-card-quota-cod" tone="amber"
              tip="Quota dell'incassato del periodo avvenuto in contrassegno: più è alta, più la cassa dipende dai versamenti del corriere" />
          )}
          {t.non_merce > 0 && (
            <MiniKpi label="Righe non-merce incassate" value={fmtEUR(t.non_merce)} testid="kpi-card-non-merce"
              tip="Importo delle righe non-merce riconosciute (es. commissioni contrassegno) sugli ordini attivi: restano nei ricavi ma non contano come pezzi venduti" />
          )}
        </div>
      </Blocco>

      <Blocco n={6} titolo="Fiscale">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniKpi label="IVA da versare" value={fmtEUR(t.iva_da_versare)} testid="kpi-card-iva-da-versare"
            tone={t.iva_da_versare < 0 ? "red" : "dark"} delta={delta(t.iva_da_versare, pt?.iva_da_versare)}
            tip="Somma delle compensazioni IVA degli ordini attivi: IVA incassata meno IVA su merce e spedizioni" />
        </div>
      </Blocco>

      {!data.consolidato && data.per_store[0]?.maturato_quota != null && (
        <div data-testid="quota-banner" className="bg-indigo-50 border border-indigo-200 rounded-[14px] px-4 py-3 text-[15px] text-indigo-900 fade-in">
          Utile da ripartire maturato nel periodo: <span className="font-mono font-bold">{fmtEUR(data.per_store[0].maturato_quota)}</span>
          {" "}(compenso fisso gestione {fmtEUR(data.store?.ripartizione?.compenso_fisso_gestione || 0)} già sottratto). Le quote maturano sull'utile realizzato (incassato), mai sull'atteso.
        </div>
      )}

      {showMerceDettaglio && (
        <MerceDettaglioDialog storeId={storeId} from={from} to={to} onClose={() => setShowMerceDettaglio(false)} />
      )}
      {showVendutoPerso && (
        <VendutoPersoDialog storeId={storeId} from={from} to={to} isMaster={isMaster}
          consolidato={data.consolidato} onClose={() => setShowVendutoPerso(false)} />
      )}

      {serie.length > 1 && (
        <div className="card p-5 fade-in" data-testid="andamento-card">
          <h2 className="text-lg font-semibold text-slate-800 font-heading mb-3">Andamento incassi nel periodo</h2>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={serie} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradIncasso" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="g" tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} width={70}
                tickFormatter={(v) => v.toLocaleString("it-IT", { maximumFractionDigits: 0 })} />
              <Tooltip formatter={(v) => [fmtEUR(v), "Incassato"]} labelStyle={{ fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="incasso" stroke="#4F46E5" strokeWidth={2} fill="url(#gradIncasso)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.consolidato && (
        <div className="card overflow-x-auto fade-in">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/80 text-left">
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Store</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Fatturato</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Incassato</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Da incassare</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">ADV</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Guadagno netto</th>
                {isMaster && <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Margine spedizioni</th>}
                {isMaster && <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Ricarico merce</th>}
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">ROAS lordo venduto</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">ROAS netto IVA venduto</th>
              </tr>
            </thead>
            <tbody>
              {data.per_store.map((s) => (
                <tr key={s.store_id} data-testid={`store-row-${s.store_nome}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors duration-150">
                  <td className="px-4 py-2.5 text-slate-700 font-medium">{s.store_nome} <span className="text-xs text-slate-400">({s.tipo})</span></td>
                  <td className="px-4 py-2.5 font-mono text-right tnum">{fmtEUR(s.totali.fatturato)}</td>
                  <td className="px-4 py-2.5 font-mono text-right tnum">{fmtEUR(s.totali.incasso)}</td>
                  <td className="px-4 py-2.5 font-mono text-right tnum text-amber-700">{fmtEUR(s.totali.da_incassare)}</td>
                  <td className="px-4 py-2.5 font-mono text-right tnum">{fmtEUR(s.totali.adv)}</td>
                  <td className={`px-4 py-2.5 font-mono text-right tnum font-semibold ${s.totali.guadagno_netto < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtEUR(s.totali.guadagno_netto)}</td>
                  {isMaster && <td className="px-4 py-2.5 font-mono text-right tnum">{s.totali.atteso_margine_spedizioni != null ? fmtEUR(s.totali.atteso_margine_spedizioni) : "—"}</td>}
                  {isMaster && <td className="px-4 py-2.5 font-mono text-right tnum">{s.totali.ricarico_merce != null ? fmtEUR(s.totali.ricarico_merce) : "—"}</td>}
                  <td className="px-4 py-2.5 font-mono text-right tnum">{fmtRoas(s.totali.roas_venduto_lordo)}</td>
                  <td className="px-4 py-2.5 font-mono text-right tnum">{fmtRoas(s.totali.roas_venduto_netto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card p-5 fade-in">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold text-slate-800 font-heading">Ripartizione per metodo di pagamento</h2>
          <div className="flex rounded-lg border border-slate-300 overflow-hidden">
            <button data-testid="payment-switch-family" onClick={() => setMode("famiglia")}
              className={`px-3 py-1.5 text-sm transition-colors duration-150 ${mode === "famiglia" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              Famiglia
            </button>
            <button data-testid="payment-switch-detail" onClick={() => setMode("dettaglio")}
              className={`px-3 py-1.5 text-sm transition-colors duration-150 ${mode === "dettaglio" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              Dettaglio
            </button>
          </div>
        </div>
        {pagamenti.length === 0 ? (
          <p className="text-sm text-slate-400">Nessun ordine nel periodo.</p>
        ) : (
          <table className="w-full text-sm" data-testid="payment-breakdown-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 text-xs font-bold uppercase tracking-wider text-slate-500">Metodo</th>
                <th className="py-2 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Ordini</th>
                <th className="py-2 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Incasso</th>
                <th className="py-2 text-xs font-bold uppercase tracking-wider text-slate-500 text-right w-1/3">Quota</th>
              </tr>
            </thead>
            <tbody>
              {pagamenti.map((p) => (
                <tr key={p.chiave} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors duration-150">
                  <td className="py-2 text-slate-700">
                    {p.chiave}
                    {p.non_mappato && <span className="ml-2 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">da mappare</span>}
                  </td>
                  <td className="py-2 font-mono text-right tnum">{fmtInt(p.ordini)}</td>
                  <td className="py-2 font-mono text-right tnum">{fmtEUR(p.incasso)}</td>
                  <td className="py-2">
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-full max-w-[180px] bg-slate-100 rounded-full h-2">
                        <div className="bg-indigo-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(p.quota * 100).toFixed(1)}%` }} />
                      </div>
                      <span className="font-mono text-xs text-slate-600 w-14 text-right">{fmtPct(p.quota)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
